import { useQuery } from "@apollo/client";
import { useCallback } from "react";
import { useRouter } from "solito/router";
import { Settings } from "@tamagui/lucide-icons";
import { FullscreenSpinner, Headline1, isWeb, OutlinedButton } from "@medbillai/ui";
import { settingsUrl } from "@medbillai/utils";
import { ScreenView, type ScreenViewProps } from "../../components/ScreenView";
import { analyticsService } from "../../lib/analytics";
import { errorHandling } from "../../lib/apollo/utils";
import { useAuth } from "../../lib/auth";
import { ReferralModal } from "../auth/components/ReferralModal";
import { useFlaggedEobsCount } from "../bill/utils/useFlaggedEobs";
import { ClaimsReviewWidget } from "./components/ClaimsReviewWidget";
import { GetHelpPrompts } from "./components/GetHelpPrompts";
import { IssuesWithActionsWidget } from "./components/IssuesWithActionsWidget";
import { PersonalizedPrompts } from "./components/PersonalizedPrompts";
import { ResolvedCase } from "./components/ResolvedCase";
import { WelcomeWidget } from "./components/WelcomeWidget";
import { getIssuesWithActionsQuery, getMostRecentClosedIssueWithoutFeedbackQuery, getPersonalizedPromptsQuery } from "./utils/queries";
import { useWelcome } from "./utils/useWelcome";
export type HomeScreenProps = {
  screenViewProps?: Omit<ScreenViewProps, "children">;
};
export function HomeScreen({
  screenViewProps
}: HomeScreenProps) {
  const {
    push
  } = useRouter();
  const {
    user,
    isAuthenticated,
    isReady
  } = useAuth();
  const {
    shouldShow: shouldShowWelcomeScreen,
    loading: welcomeScreenLoading,
    refetch: refetchWelcomeScreen
  } = useWelcome();
  const {
    data: personalizedPromptsData,
    error: personalizedPromptsError,
    refetch: refetchPersonalizedPrompts
  } = useQuery(getPersonalizedPromptsQuery, {
    context: errorHandling("caller"),
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated || !isReady
  });
  const {
    data: issuesWithActionsData,
    refetch: refetchIssuesWithActions,
    error: issuesWithActionsError
  } = useQuery(getIssuesWithActionsQuery, {
    context: errorHandling("caller"),
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated || !isReady
  });
  const {
    data: resolvedCaseData,
    error: resolvedCaseError,
    refetch: refetchResolvedCase
  } = useQuery(getMostRecentClosedIssueWithoutFeedbackQuery, {
    variables: {
      userId: user?.id ?? ""
    },
    context: errorHandling("caller"),
    notifyOnNetworkStatusChange: true,
    skip: !user?.id
  });
  const {
    count: flaggedEobsCount,
    error: flaggedEobsError,
    refetch: refetchFlaggedEobs
  } = useFlaggedEobsCount();
  const handleRefresh = async () => {
    await Promise.all([refetchPersonalizedPrompts(), refetchIssuesWithActions(), refetchResolvedCase(), refetchFlaggedEobs(), refetchWelcomeScreen()]);
  };

  // Check if we have all the data we need (not just any data)
  const hasAllData = (personalizedPromptsData || personalizedPromptsError) && (issuesWithActionsData || issuesWithActionsError) && (resolvedCaseData || resolvedCaseError) && (flaggedEobsCount !== undefined || flaggedEobsError);

  // Show loading when we're missing essential data OR when welcome screen is
  // loading
  const shouldShowLoading = welcomeScreenLoading || !hasAllData;
  const issuesWithActions = issuesWithActionsData?.me?.issues ?? [];
  const closedEvent = resolvedCaseData?.issue__ClosedEvent?.[0] ?? null;
  const personalizedPrompts = personalizedPromptsData?.getPersonalizedPrompts?.prompts ?? [];
  const handleSettingsPress = useCallback(() => {
    analyticsService.trackEvent("ClickedSettings", {});
    push(settingsUrl());
  }, [push]);
  return <ScreenView noPadding centerHorizontal onRefresh={handleRefresh} widthLimit={650} {...shouldShowWelcomeScreen ? {
    backgroundRadialGradient: true,
    backgroundRadialGradientProps: {
      backgroundColor: "$surface1"
    },
    stackProps: {
      marginBottom: "$12"
    }
  } : {
    backgroundColor: "$surface1"
  }} {...screenViewProps} stackProps={{
    gap: 0
  }} data-sentry-element="ScreenView" data-sentry-component="HomeScreen" data-sentry-source-file="screen.tsx">
      {shouldShowLoading ? <FullscreenSpinner /> : shouldShowWelcomeScreen ? <>
          {/* Web already has settings access via the top bar. */}
          {isWeb ? null : <OutlinedButton marginRight="$4" marginTop="$4" paddingVertical="$0" circular scaleIcon={1.25} size="$3" icon={Settings} alignSelf="flex-end" onPress={handleSettingsPress} />}
          <WelcomeWidget />
          <GetHelpPrompts />
        </> : <>
          {isWeb ? <Headline1 color="$textDefault" pt="$6" px="$4" pb="$2" $gtMd={undefined}>
              Welcome
            </Headline1> : null}
          <IssuesWithActionsWidget issues={issuesWithActions} />
          <ClaimsReviewWidget count={flaggedEobsCount} error={flaggedEobsError} />
          <ResolvedCase
      // Even though we only display one resolved case at a time, we need
      // to give it a key so that if we swap out to another case (because
      // we've reviewed the first one), React knows to give us fresh
      // component state. See ENG-5514, where the lack of this caused
      // second cases to not open the feedback dialog, because their state
      // said they were already submitted.
      key={closedEvent?.id} closedEvent={closedEvent} error={resolvedCaseError} />
          <PersonalizedPrompts prompts={personalizedPrompts} error={personalizedPromptsError} />
          <GetHelpPrompts />
        </>}
      <ReferralModal data-sentry-element="ReferralModal" data-sentry-source-file="screen.tsx" />
    </ScreenView>;
}